import { IBlogIntegrationData, IFontData, ImageRatioType, IPagePrimaryElement, SelfPosition, WidgetPlaceType } from "./index";
import { IAnimatedWidget } from "./widget-style-data";

export interface IBlogRollWidgetData extends IPagePrimaryElement, IBlogIntegrationData {
    readonly type: "BlogRollWidget";
    placeType: WidgetPlaceType;
    id?: string | null;
    showAuthorName?: boolean;
    showDate?: boolean;
    showDescription?: boolean;
    showCategories?: boolean;
    withoutImage?: boolean;
    layout: BlogRollWidgetLayout;
    style: BlogRollWidgetStyle;
    imgRatio?: ImageRatioType;
    pagination: IBlogRollPaginationData;
    defaultCategory?: string | null;
}

export interface IBlogRollPaginationData {
    type: BlogRollPaginationType;
    articlesPerPage?: number;
    style: IBlogRollPaginationStyle;
    showPagination?: boolean;
}

export interface IBlogRollPaginationStyle {
    buttonColor?: string;
    activeButtonColor?: string;
    alignment?: SelfPosition;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IBlogRollEditorialWidgetStyle extends IBlogRollClassicWidgetStyle {
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IBlogRollMagazineWidgetStyle extends IBaseBlogRollWidgetStyle {
}

export interface IBlogRollClassicWidgetStyle extends IBaseBlogRollWidgetStyle {
    excerptColor: string;
    excerptFont?: IFontData;
}

export interface IBlogRollAllArticleContentWidgetStyle extends IBaseBlogRollWidgetStyle {
    padding: string;
    heading2Color?: string;
    heading3Color?: string;
    heading4Color?: string;
    heading2Size?: string;
    heading3Size?: string;
    heading4Size?: string;
    headingFont: IFontData;
    subheadingFont: IFontData;
    normalTextFont: IFontData;
}

export interface IBaseBlogRollWidgetStyle extends IAnimatedWidget {
    titleColor: string;
    hoverTextColor: string;
    borderColor?: string;
    borderWidth?: string;
    postBackground: string;
    postListBackground: string;
    titleFont: IFontData;
    commonFont: IFontData;
    authorColor?: string;
    publishDateColor?: string;
    linesForTitle: number;
    linesForExcerpt: number;
}

export abstract class BlogRollWidgetData {

    public static get defaultEditorialStyle(): IBlogRollEditorialWidgetStyle {
        return {
            titleColor: "#202020",
            excerptColor: "#202020",
            hoverTextColor: "#202020",
            authorColor: "#202020",
            publishDateColor: "#202020",
            borderColor: "#DBDBDB",
            borderWidth: "1px",
            postBackground: "#FFF",
            postListBackground: "#FFF",
            commonFont: {
                family: "Open Sans"
            },
            titleFont: {
                size: "2rem",
                weight: "700"
            },
            excerptFont: {
                size: "1rem",
                weight: "400",
                lineHeight: "1.5em"
            },
            linesForTitle: 2,
            linesForExcerpt: 4,
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                }
            }
        };
    }

    public static get defaultMagazineStyle(): IBlogRollMagazineWidgetStyle {
        return {
            titleColor: "#FFF",
            authorColor: "#FFF",
            publishDateColor: "#FFF",
            hoverTextColor: "#FFF",
            postBackground: "rgba(0, 0, 0, 0.4)",
            postListBackground: "#FFF",
            commonFont: {
                family: "Open Sans"
            },
            titleFont: {
                size: "1.5rem",
                weight: "700"
            },
            linesForTitle: 2,
            linesForExcerpt: 4,
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                }
            }
        };
    }

    public static get defaultAllArticleContentStyle(): IBlogRollAllArticleContentWidgetStyle {
        return {
            hoverTextColor: "#202020",
            borderColor: "#DBDBDB",
            postBackground: "#FFF",
            postListBackground: "#FFF",
            titleColor: "#202020",
            linesForExcerpt: 0,
            linesForTitle: 0,
            commonFont: {
                family: "Open Sans"
            },
            titleFont: {
                size: "2rem",
                weight: "700"
            },
            borderWidth: "1px",
            padding: "5px",
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                }
            },
            headingFont: {
                family: ""
            },
            subheadingFont: {
                family: ""
            },
            normalTextFont: {
                family: ""
            }
        };
    }

    public static get defaultStyle(): IBlogRollClassicWidgetStyle {
        return {
            titleColor: "#202020",
            excerptColor: "#202020",
            hoverTextColor: "#202020",
            authorColor: "#202020",
            publishDateColor: "#202020",
            borderColor: "#DBDBDB",
            borderWidth: "1px",
            postBackground: "#FFF",
            postListBackground: "#FFF",
            commonFont: {
                family: "Open Sans"
            },
            titleFont: {
                size: "2rem",
                weight: "700"
            },
            excerptFont: {
                size: "1rem",
                weight: "400",
                lineHeight: "1.5em"
            },
            linesForTitle: 2,
            linesForExcerpt: 4,
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                }
            }
        };
    }

    public static get defaultData(): IBlogRollWidgetData {
        return {
            type: "BlogRollWidget",
            placeType: "Section",
            blogId: "",
            primaryElement: false,
            showAuthorName: true,
            showDate: true,
            showDescription: true,
            showCategories: true,
            layout: BlogRollWidgetLayout.Classic,
            style: this.defaultStyle,
            imgRatio: "original",
            withoutImage: false,
            pagination: BlogRollWidgetData.defaultPagination,
            defaultCategory: null
        };
    }

    public static get defaultPagination(): IBlogRollPaginationData {
        return {
            type: BlogRollPaginationType.ShowAll,
            style: {}
        };
    }

    public static get defaultLoadMorePagination(): IBlogRollPaginationData {
        return {
            type: BlogRollPaginationType.LoadMore,
            articlesPerPage: 10,
            style: {
                alignment: "center",
                buttonColor: "#3B507D"
            }
        };
    }

    public static get defaultShowPerPagePagination(): IBlogRollPaginationData {
        return {
            type: BlogRollPaginationType.XArticlesPerPage,
            articlesPerPage: 10,
            showPagination: true,
            style: {
                buttonColor: "#7B828E",
                activeButtonColor: "#3B507D"
            }
        };
    }

    public static localScriptRerenderNeeded(leftHandSide?: IBlogRollWidgetData | null, rightHandSide?: IBlogRollWidgetData | null): boolean {
        return leftHandSide?.id === rightHandSide?.id &&
            leftHandSide?.blogId === rightHandSide?.blogId &&
            leftHandSide?.showAuthorName === rightHandSide?.showAuthorName &&
            leftHandSide?.showCategories === rightHandSide?.showCategories &&
            leftHandSide?.showDate === rightHandSide?.showDate &&
            leftHandSide?.showDescription === rightHandSide?.showDescription &&
            leftHandSide?.withoutImage === rightHandSide?.withoutImage &&
            leftHandSide?.imgRatio === rightHandSide?.imgRatio &&
            leftHandSide?.layout === rightHandSide?.layout &&
            leftHandSide?.pagination.type === rightHandSide?.pagination.type &&
            leftHandSide?.pagination.articlesPerPage === rightHandSide?.pagination.articlesPerPage &&
            leftHandSide?.pagination.showPagination === rightHandSide?.pagination.showPagination &&
            leftHandSide?.defaultCategory === rightHandSide?.defaultCategory;
    }

}

export enum BlogRollWidgetLayout {
    Classic = "classic",
    Magazine = "magazine",
    Editorial = "editorial",
    AllArticleContent = "all-article-content",
}

export interface IBlogRollWidgetDataShort {
    showAuthorName?: boolean;
    showDate?: boolean;
    showDescription?: boolean;
    showCategories?: boolean;
    withoutImage?: boolean;
    layout: BlogRollWidgetLayout;
    imgRatio?: ImageRatioType;
    paginationType?: BlogRollPaginationType;
    articlesPerPage?: number;
    showPagination?: boolean;
    defaultCategory?: string | null;
}

export type BlogRollWidgetStyle =
    IBlogRollClassicWidgetStyle
    | IBlogRollMagazineWidgetStyle
    | IBlogRollEditorialWidgetStyle
    | IBlogRollAllArticleContentWidgetStyle;

export enum BlogRollPaginationType {
    LoadMore = "LoadMore",
    XArticlesPerPage = "XArticlesPerPage",
    ShowAll = "ShowAll"
}
